import { TEMPLATE_TYPES } from 'cpool-constants';
import {
    Template_DivisionFooterCreated,
    Template_DivisionHeaderCreated,
    Template_DivisionMailCreated,
    Template_MailCreated,
    Template_MailFooterCreated,
    Template_MailHeaderCreated,
    Template_MailSignatureCreated,
    Template_OfferMailCreated,
    Template_SystemMailCreated,
} from 'cpool-events';
import { divisionId, schemaId } from 'cpool-schemas';
import { z } from 'zod';

const defaultTemplateSchema = z.object({
    active: z.boolean().nullish(),
    aggregateVersion: z.coerce.number().nullish(),
    createdAt: z.coerce.date(),
    id: schemaId,
    name: z.string().nullable(),
    priority: z.number().nullable(),
});

export const schemaTemplatesMail = z
    .object({
        data: Template_MailCreated.prototype
            .getSchema()
            .omit({ active: true, name: true, priority: true, technicalDescription: true, type: true }),
        technicalDescription: z.preprocess((val) => (!val ? null : val), z.string().nullable().optional()),
        type: z.literal(TEMPLATE_TYPES.MAIL),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesMailHeader = z
    .object({
        data: Template_MailHeaderCreated.prototype.getSchema().omit({ type: true }),
        type: z.literal(TEMPLATE_TYPES.MAIL_HEADER),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesMailFooter = z
    .object({
        data: Template_MailFooterCreated.prototype.getSchema().omit({ type: true }),
        type: z.literal(TEMPLATE_TYPES.MAIL_FOOTER),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesDivisionHeader = z
    .object({
        data: Template_DivisionHeaderCreated.prototype.getSchema().omit({ divisionId: true, type: true }),
        divisionId,
        type: z.literal(TEMPLATE_TYPES.DIVISION_HEADER),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesDivisionFooter = z
    .object({
        data: Template_DivisionFooterCreated.prototype.getSchema().omit({ divisionId: true, type: true }),
        divisionId,
        type: z.literal(TEMPLATE_TYPES.DIVISION_FOOTER),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesMailSignature = z
    .object({
        data: Template_MailSignatureCreated.prototype
            .getSchema()
            .omit({ active: true, name: true, priority: true, type: true, users: true }),
        type: z.literal(TEMPLATE_TYPES.MAIL_SIGNATURE),
        users: z.array(z.string()).nullable(),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesSystemMail = z
    .object({
        data: Template_SystemMailCreated.prototype.getSchema().omit({ name: true, type: true }),
        type: z.literal(TEMPLATE_TYPES.SYSTEM_MAIL),
    })
    .merge(defaultTemplateSchema);

export const schemaTemplatesDivisionMail = z
    .object({
        data: Template_DivisionMailCreated.prototype
            .getSchema()
            .omit({ active: true, divisionId: true, name: true, priority: true, type: true }),
        divisionId,
        technicalDescription: z.string().min(1).nullish(),
        type: z.literal(TEMPLATE_TYPES.DIVISION_MAIL),
    })
    .merge(defaultTemplateSchema)
    // the name should not be null otherwise we would need to change the template events
    .merge(z.object({ name: Template_DivisionMailCreated.prototype.getSchema().shape.name }));

export const schemaTemplatesOffer = z
    .object({
        data: Template_OfferMailCreated.prototype
            .getSchema()
            .omit({ active: true, name: true, priority: true, technicalDescription: true, type: true }),
        technicalDescription: z.preprocess((val) => (!val ? null : val), z.string().nullable().optional()),
        type: z.literal(TEMPLATE_TYPES.OFFER_MAIL),
    })
    .merge(defaultTemplateSchema);

export type TemplateMailHeader = z.infer<typeof schemaTemplatesMailHeader>;

export type TemplateMailFooter = z.infer<typeof schemaTemplatesMailFooter>;

export type TemplateDivisionHeader = z.infer<typeof schemaTemplatesDivisionHeader>;

export type TemplateDivisionFooter = z.infer<typeof schemaTemplatesDivisionFooter>;

export type TemplateMailSignature = z.infer<typeof schemaTemplatesMailSignature>;

export type TemplateSystemMail = z.infer<typeof schemaTemplatesSystemMail>;

export type TemplateDivisionMail = z.infer<typeof schemaTemplatesDivisionMail>;

export type TemplateOfferMail = z.infer<typeof schemaTemplatesOffer>;
